import {
    Button, Checkbox, CheckboxOnChangeData, CheckboxProps, Divider, Field, Input,
    makeStyles, Select, shorthands, Spinner, Table, TableBody,
    TableCell,
    TableCellLayout, TableHeader,
    TableHeaderCell,
    TableRow, Text, ToggleButton, tokens,
    useId
} from "@fluentui/react-components";
import {RouteContext} from "../../RouteContext";
import {
    BooleanStatePair,
    InputStringStatePair,
    StatePair,
    UndefinedStatePair, useBoolean,
    useError,
    useInputString,
    useStatePair,
    useUndefined
} from "../../../type/States";
import React, {JSX, useCallback, useEffect, useState} from "react";
import {Column, Row} from "./Layout";
import {
    AddFilled, ArrowSyncFilled, CheckmarkFilled, ChevronDoubleLeftFilled, ChevronDoubleRightFilled,
    Dismiss12Filled, DismissFilled,
    EditFilled,
    FilterFilled,
    OpenFilled,
    TextSortAscendingFilled
} from "@fluentui/react-icons";
import {SearchCondition, SearchConditionDescription, SearchConditionValuePrerequisite} from "../../../io/SearchCondition";
import {SortOrder, SortOrderDescription} from "../../../io/SortOrder";
import {BigIntegers, StringKeyOf, Types} from "../../../type/Types";
import {SearchConditionElement} from "../../../io/SearchConditionElement";
import {SortOrderElement} from "../../../io/SortOrderElement";
import {DefaultCountIO, DefaultDeleteIdsIO, DefaultDeleteIO, DefaultListIO} from "../../../io/DefaultIO";
import SheetButton, {SheetButtonDownload, SheetButtonUploadMethod} from "./SheetButton";
import UpdateShoppingsStateButton from "./UpdateShoppingsState";
import {convertTimezone} from "../../../utils/convertTimeZone";
import * as timers from "node:timers";

const useStyles = makeStyles({
    header: {
        flexWrap: 'wrap'
    },
    actionDelete: {
        backgroundColor: tokens.colorStatusDangerBackground1,
        ':hover': {
            backgroundColor: tokens.colorStatusDangerBackground3Hover
        },
        ':active': {
            backgroundColor: tokens.colorStatusDangerBackground3Hover
        },
        ...shorthands.borderColor(tokens.colorStatusDangerBorder1)
    },
    table: {
        width: 'max-content',
        tableLayout: 'auto', // 셀 너비 조정
    },
    paginationContainer: {
        flexWrap: 'wrap',
    },
    paginationButton: {
        minWidth: 'unset',
        maxWidth: 'unset',
        width: '48px',
        height: '48px',
    },
    filterDivider: {
        flexGrow: 'unset'
    },
    filterListItemButton: {
        display: 'flex',
        flexDirection: 'column',
        width: '240px'
    },
    orderDivider: {
        flexGrow: 'unset'
    },
    orderListItemButton: {
        display: 'flex',
        flexDirection: 'column',
        width: '240px'
    },
    tableCellLayoutNowrap: {
        whiteSpace: 'nowrap',
        display: 'block'
    },
})

type Panel = 'List' | 'SearchCondition' | 'SortOrder'

type ValueObjectConstraint<Table> = Table & object

export type ListPageProps<VO extends ValueObjectConstraint<Table>, Table = VO> = RouteContext & {
    description: Record<keyof VO, string>
    excluded?: Set<keyof VO>
    list: DefaultListIO<VO, Table>
    count: DefaultCountIO<Table>
    delete?: DefaultDeleteIO
    deleteIds?: DefaultDeleteIdsIO
    updateIds?: (ids:bigint[],wishState:boolean) => void
    postHref?: string
    detailHref: (item: VO) => string
    editHref?: (item: VO) => string
    identify?: (item: VO) => bigint
    itemValue?: (item: VO, key: keyof VO) => string | JSX.Element | undefined
    uploadSheetUrls?: { name: string, url: string, method: SheetButtonUploadMethod }[]
    downloadSheetUrl?: (searchConditions: SearchConditionElement<Table>[], sortOrders: SortOrderElement<Table>[], offset: bigint, count:number) => string
    downloadSheetExampleUrls?: { name: string, url: string }[]
    excelExportUrl?: SheetButtonDownload<Table>
    initialSearchConditions?: SearchConditionElement<Table>[]
    initialSortOrders?: SortOrderElement<Table>[]
}

function identify<VO>(item: VO, identify: ((item: VO) => bigint) | undefined): bigint {
    if (identify === undefined) {
        const id: any | undefined = (item as any).id
        if (id === undefined) {
            throw new Error('id is undefined')
        } else if (typeof id !== 'bigint') {
            throw new Error(`Invalid id type ${typeof id}`)
        } else {
            return id
        }
    } else {
        return identify(item)
    }
}

export function ListPage<VO extends ValueObjectConstraint<Table>, Table>(props: ListPageProps<VO, Table>) {
    const styles = useStyles()
    const panelState = useStatePair<Panel>('List')
    const listState = useUndefined<VO[]>()
    const listErrorState = useError()
    const countState = useUndefined<bigint>()
    const countErrorState = useError()
    const isCheckEnabledState = useBoolean(false)
    const checkedItemsState = useStatePair(new Set<bigint>())
    const offsetState = useStatePair<bigint>(0n)
    const searchConditionsState = useStatePair<SearchConditionElement<Table>[]>(props.initialSearchConditions ?? [])
    const sortOrdersState = useStatePair<SortOrderElement<Table>[]>(props.initialSortOrders ?? [])
    //UTC KST
    const selectId = useId();
    const timeZones: string[] = ['UTC','KST']
    const selectedTimeZone = useStatePair<string>(timeZones[0])

    useEffect(() => {
        if (listState.value !== undefined) {
            return
        }

        props.list(searchConditionsState.value, sortOrdersState.value, 40, offsetState.value)
            .then(listState.setDefinedValue)
            .catch(listErrorState.setDefinedValue)
        console.log(listState.value)
    }, [listState.value, offsetState.value, searchConditionsState.value, sortOrdersState.value]);

    useEffect(() => {
        props.list(searchConditionsState.value, sortOrdersState.value, 40, offsetState.value)
            .then(listState.setDefinedValue)
            .catch(listErrorState.setDefinedValue)


            console.log(listState.value)
    }, [offsetState.value, searchConditionsState.value, sortOrdersState.value]);

    useEffect(() => {
        if (!isCheckEnabledState.value) {
            checkedItemsState.reset()
        }
        console.log(listState.value)
    }, [isCheckEnabledState.value]);

    useEffect(() => {
        if (listState.value !== undefined) {
            return
        }

        props.count(searchConditionsState.value)
            .then(countState.setDefinedValue)
            .catch(countErrorState.setDefinedValue)
        console.log(listState.value)
    }, [listState.value]);

    useEffect(() => {
        props.count(searchConditionsState.value)
            .then(countState.setDefinedValue)
            .catch(countErrorState.setDefinedValue)
    }, [offsetState.value, searchConditionsState.value, sortOrdersState.value]);

    const onDeleteClick = () => {
        const _delete = props.delete
        if (_delete === undefined) {
            return
        }
        if (checkedItemsState.value.size === 0) {
            window.alert('삭제할 항목을 선택해 주세요.')
            return
        }
        if (!window.confirm(`${checkedItemsState.value.size}행을 삭제하시겠습니까?`)) {
            return
        }



        Promise.all([...checkedItemsState.value].map(_delete))
            .then(() => {
                window.alert('삭제되었습니다.')
                listState.unset()
            })
            .catch(() => {
                window.alert('삭제 중 오류가 발생했습니다.')
            })
    }

    const onDeleteIdsClick = () => {
        const _deleteIds = props.deleteIds
        if (_deleteIds === undefined) {
            return
        }
        if (checkedItemsState.value.size === 0) {
            window.alert('삭제할 항목을 선택해 주세요.')
            return
        }
        if (!window.confirm(`${checkedItemsState.value.size}행을 삭제하시겠습니까?`)) {
            return
        }

        _deleteIds([...checkedItemsState.value]).then(()=>{
            alert('삭제에 성공했습니다.')
            listState.unset()
        }).catch(err=>{
            alert('삭제에 실패했습니다.')
        })
    }

    const onUpdate = (wishState: boolean) => {

        const _updateIds = props.updateIds;
        if(_updateIds === undefined) return

        if (checkedItemsState.value.size === 0) {
            window.alert('수정할 항목을 선택해 주세요.')
            return
        }

        _updateIds([...checkedItemsState.value], wishState)

    }

    return <>
        <listErrorState.Dialog {...props} />
        <countErrorState.Dialog {...props} />
        <Column gap={'16px'}>
            <Row gap={'8px'}>
                {props.postHref !== undefined && <>
                    <Button
                        as={'a'}
                        appearance={'primary'}
                        icon={<AddFilled />}
                        href={props.postHref}
                        target={'_blank'}>
                        새 항목
                    </Button>
                </>}
                <Button
                    icon={<ArrowSyncFilled />}
                    onClick={() => listState.unset()} />
                <Button
                    icon={<FilterFilled />}
                    onClick={() => panelState.setValue('SearchCondition')} />
                <Button
                    icon={<TextSortAscendingFilled />}
                    onClick={() => panelState.setValue('SortOrder')} />
                <Select id={selectId}
                        onChange={(event,data)=>{selectedTimeZone.setValue(data.value)}}
                        value={selectedTimeZone.value}
                        {...props}>
                    {timeZones.map((v,idx)=>(
                        <option key={idx}>{v}</option>
                    ))}
                </Select>
                <Divider vertical style={{ flexGrow: 'unset' }} />
                {isCheckEnabledState.value && <>
                    <Button
                        icon={<DismissFilled />}
                        onClick={isCheckEnabledState.setFalse}>
                        선택 해제
                    </Button>
                </>}
                {!isCheckEnabledState.value && <>
                    <Button
                        icon={<CheckmarkFilled />}
                        onClick={isCheckEnabledState.setTrue}>
                        선택
                    </Button>
                </>}
                {isCheckEnabledState.value && props.delete !== undefined && <>
                    <Button
                        className={styles.actionDelete}
                        icon={<DismissFilled />}
                        onClick={onDeleteClick}>
                        {checkedItemsState.value.size}개 삭제
                    </Button>
                </>}
                {isCheckEnabledState.value && props.deleteIds !== undefined && <>
                    <Button
                        className={styles.actionDelete}
                        icon={<DismissFilled />}
                        onClick={onDeleteIdsClick}>
                        {checkedItemsState.value.size}개 삭제
                    </Button>
                </>}
                {isCheckEnabledState.value && props.updateIds !== undefined && <>
                    <UpdateShoppingsStateButton
                        {...props}
                         onUpdate={onUpdate}
                    />
                </>}
                {(props.uploadSheetUrls !== undefined || props.downloadSheetUrl !== undefined || props.downloadSheetExampleUrls !== undefined) && <>
                        <SheetButton
                            {...props}
                            searchConditions={searchConditionsState.value}
                            sortOrders={sortOrdersState.value}
                            offset={offsetState.value}
                            count={Number(countState.value?.toString().split('n')[0]) ?? 40}
                        />
                </>}
            </Row>
            <Header
                styles={styles}
                description={props.description}
                searchConditionsState={searchConditionsState}
                sortOrdersState={sortOrdersState} />
            <ListPanel
                {...props}
                styles={styles}
                size={40}
                panelState={panelState}
                listState={listState}
                countState={countState}
                offsetState={offsetState}
                isCheckEnabledState={isCheckEnabledState}
                checkedItemsState={checkedItemsState}
                timeZone={selectedTimeZone.value}
            />
            <SearchConditionPanel
                styles={styles}
                description={props.description}
                excluded={props.excluded ?? new Set<keyof VO>()}
                panelState={panelState}
                searchConditionsState={searchConditionsState} />
            <SortOrderPanel
                styles={styles}
                description={props.description}
                excluded={props.excluded ?? new Set<keyof VO>()}
                panelState={panelState}
                sortOrderState={sortOrdersState} />
        </Column>
    </>
}

type HeaderProps<VO extends ValueObjectConstraint<Table>, Table> = {
    styles: ReturnType<typeof useStyles>
    description: Record<keyof VO, string>
    searchConditionsState: StatePair<SearchConditionElement<Table>[]>
    sortOrdersState: StatePair<SortOrderElement<Table>[]>
}

function Header<VO extends ValueObjectConstraint<Table>, Table>(props: HeaderProps<VO, Table>) {
    return <>
        <Row
            className={props.styles.header}
            gap={'8px'}>
            {props.searchConditionsState.value.map((value, index) => (
                <HeaderSearchCondition
                    key={`list-header-search-condition-${value.name as string}-${value.condition}-${value.condition}`}
                    description={props.description}
                    searchConditionsState={props.searchConditionsState}
                    value={value}
                    index={index} />
            ))}
            {props.sortOrdersState.value.map((value, index) => (
                <HeaderSortOrder
                    key={`list-header-sort-order-${value.name as string}-${value.order}`}
                    description={props.description}
                    sortOrdersState={props.sortOrdersState}
                    value={value}
                    index={index} />
            ))}
        </Row>
    </>
}

type HeaderSearchConditionProps<VO extends ValueObjectConstraint<Table>, Table> = {
    description: Record<keyof VO, string>
    searchConditionsState: StatePair<SearchConditionElement<Table>[]>
    value: SearchConditionElement<Table>,
    index: number
}

function HeaderSearchCondition<VO extends ValueObjectConstraint<Table>, Table>(props: HeaderSearchConditionProps<VO, Table>) {
    return <>
        <Button
            size={'small'}
            icon={<Dismiss12Filled />}
            shape={'circular'}
            onClick={() => props.searchConditionsState.setValue(
                props.searchConditionsState.value.filter((o, i) => i !== props.index)
            )}>
            {`${props.description[props.value.name]} "${props.value.value}" ${SearchConditionDescription[props.value.condition]}`}
        </Button>
    </>
}

type HeaderSortOrderProps<VO extends ValueObjectConstraint<Table>, Table> = {
    description: Record<keyof VO, string>
    sortOrdersState: StatePair<SortOrderElement<Table>[]>
    value: SortOrderElement<Table>
    index: number
}

function HeaderSortOrder<VO extends ValueObjectConstraint<Table>, Table>(props: HeaderSortOrderProps<VO, Table>) {
    return <>
        <Button
            size={'small'}
            shape={'circular'}
            onClick={() => props.sortOrdersState.setValue(
                props.sortOrdersState.value.filter((o, i) => i !== props.index)
            )}>
            {`${props.description[props.value.name]} ${SortOrderDescription[props.value.order]}`}
        </Button>
    </>
}

type ListPanelProps<VO extends ValueObjectConstraint<Table>, Table> = RouteContext & {
    styles: ReturnType<typeof useStyles>
    description: Record<keyof VO, string>
    delete?: DefaultDeleteIO
    detailHref: (item: VO) => string
    editHref?: (item: VO) => string
    identify?: (item: VO) => bigint
    itemValue?: (item: VO, key: keyof VO) => string | JSX.Element | undefined
    size: number
    panelState: StatePair<Panel>
    listState: UndefinedStatePair<VO[]>
    countState: UndefinedStatePair<bigint>
    offsetState: StatePair<bigint>
    isCheckEnabledState: BooleanStatePair
    checkedItemsState: StatePair<Set<bigint>>
    timeZone:string
}

function ListPanel<VO extends ValueObjectConstraint<Table>, Table>(props: ListPanelProps<VO, Table>) {
    const styles = props.styles
    const isCheckedState = useStatePair<CheckboxProps['checked']>(false)

    useEffect(() => {
        if (props.listState.value === undefined) {
            isCheckedState.setValue(false)
        } else if (props.listState.value.length === 0) {
            isCheckedState.setValue(false)
        } else if (props.checkedItemsState.value.size === 0) {
            isCheckedState.setValue(false)
        } else if (props.checkedItemsState.value.size === props.listState.value.length) {
            isCheckedState.setValue(true)
        } else {
            isCheckedState.setValue('mixed')
        }
    }, [props.listState.value, props.checkedItemsState.value]);

    if (props.panelState.value !== 'List') {
        return <></>
    }
    if (props.listState.value === undefined) {
        return <Spinner />
    }

    const onCheckChange = (ev: React.ChangeEvent<HTMLInputElement>, data: CheckboxOnChangeData) => {
        isCheckedState.setValue(data.checked)
        if (data.checked === true) {
            props.checkedItemsState.setValue(new Set<bigint>((props.listState.value ?? []).map(item => identify(item, props.identify))))
        } else if (data.checked === false) {
            props.checkedItemsState.reset()
        }
    }

    return <>
        <Column gap={'16px'}>
            <Table className={styles.table}>
                <TableHeader>
                    <TableRow>
                        {props.isCheckEnabledState.value && <>
                            <TableHeaderCell>
                                <Checkbox
                                    checked={isCheckedState.value}
                                    onChange={onCheckChange} />
                            </TableHeaderCell>
                        </>}
                        {Object.keys(props.description).map(key => (
                            <ListTableHeaderCell
                                key={`list-table-header-cell-${key}`}
                                description={props.description}
                                itemKey={key as keyof VO} />
                        ))}
                        <TableHeaderCell>
                            동작
                        </TableHeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {props.listState.value.map(item => (
                        <ListTableRow
                            {...props}
                            key={`list-table-row-${identify(item, props.identify)}`}
                            item={item}
                            itemValue={props.itemValue}
                            timeZone={props.timeZone}
                        />
                    ))}
                </TableBody>
            </Table>
            <ListPagination {...props} />
        </Column>
    </>
}

type ListTableHeaderCellProps<VO extends ValueObjectConstraint<Table>, Table> = {
    description: Record<keyof VO, string>
    itemKey: keyof VO
}

function ListTableHeaderCell<VO extends ValueObjectConstraint<Table>, Table>(props: ListTableHeaderCellProps<VO, Table>) {
    return <>
        <TableHeaderCell>
            {props.description[props.itemKey]}
        </TableHeaderCell>
    </>
}

type ListTableRowProps<VO extends ValueObjectConstraint<Table>, Table> = RouteContext & {
    delete?: DefaultDeleteIO
    deleteIds?: DefaultDeleteIdsIO
    detailHref: (item: VO) => string
    editHref?: (item: VO) => string
    identify?: (item: VO) => bigint
    itemValue?: (item: VO, key: keyof VO) => string | JSX.Element | undefined
    listState: UndefinedStatePair<VO[]>
    isCheckEnabledState: BooleanStatePair
    checkedItemsState: StatePair<Set<bigint>>
    item: VO
    timeZone:string
}

function ListTableRow<VO extends ValueObjectConstraint<Table>, Table>(props: ListTableRowProps<VO, Table>) {
    const deleteErrorState = useError()

    const onCheckChange = (ev: React.ChangeEvent<HTMLInputElement>, data: CheckboxOnChangeData) => {
        const acc = new Set<bigint>(props.checkedItemsState.value)
        if (data.checked === true) {
            acc.add(identify(props.item, props.identify))
        } else if (data.checked === false) {
            acc.delete(identify(props.item, props.identify))
        }

        props.checkedItemsState.setValue(acc)
    }

    const onDeleteClick = () => {
        if (props.delete === undefined) {
            return // 어떻게 클릭했을까
        }
        if (!window.confirm('삭제하시겠습니까?')) {
            return
        }
        props.delete(identify(props.item, props.identify))
            .then(props.listState.unset)
            .catch(deleteErrorState.setDefinedValue)
    }

    const onDeleteIdsClick = () => {

        if (props.deleteIds === undefined) {
            return
        }
        if (!window.confirm('삭제하시겠습니까?')) {
            return
        }

        props.deleteIds([identify(props.item, props.identify)])
            .then(props.listState.unset)
            .catch(deleteErrorState.setDefinedValue)
    }

    return <>
        <deleteErrorState.Dialog {...props} />
        <TableRow>
            {props.isCheckEnabledState.value && <>
                <TableCell>
                    <TableCellLayout>
                        <Checkbox
                            checked={props.checkedItemsState.value.has(identify(props.item, props.identify))}
                            onChange={onCheckChange} />
                    </TableCellLayout>
                </TableCell>
            </>}
            {Object.keys(props.item).map(key => (
                <ListTableRowCell
                    key={`list-table-row-${identify(props.item, props.identify)}-cell-${key}`}
                    item={props.item}
                    itemKey={key as keyof VO}
                    itemValue={props.itemValue}
                    timeZone={props.timeZone}
                />
            ))}
            <TableCell>
                <TableCellLayout>
                    <Row>
                        <Button
                            as={'a'}
                            appearance={'subtle'}
                            icon={<OpenFilled />}
                            href={props.detailHref(props.item)}
                            target={'_blank'} />
                        {props.editHref !== undefined && <>
                            <Button
                                as={'a'}
                                appearance={'subtle'}
                                icon={<EditFilled />}
                                href={props.editHref(props.item)}
                                target={'_blank'} />
                        </>}
                        {props.delete !== undefined && <>
                            <Button
                                appearance={'subtle'}
                                icon={<DismissFilled />}
                                onClick={onDeleteClick} />
                        </>}
                        {props.deleteIds !== undefined && <>
                            <Button
                                appearance={'subtle'}
                                icon={<DismissFilled />}
                                onClick={onDeleteIdsClick} />
                        </>}
                    </Row>
                </TableCellLayout>
            </TableCell>
        </TableRow>
    </>
}

type ListTableRowCellProps<VO extends ValueObjectConstraint<Table>, Table> = {
    item: VO
    itemKey: keyof VO
    itemValue?: (item: VO, key: keyof VO) => string | JSX.Element | undefined
    timeZone: string
}

function ListTableRowCell<VO extends ValueObjectConstraint<Table>, Table>(props: ListTableRowCellProps<VO, Table>) {
    let cell: JSX.Element

    const styles = useStyles()

    if (props.itemValue === undefined) {

        if( props.itemKey.toString() !== 'exposedAt' && props.itemKey.toString().includes('At') ) {
            cell = <Text>{convertTimezone(props.item[props.itemKey],props.timeZone)}</Text>
        }
        else{
            cell = <Text>{Types.present(props.item[props.itemKey])}</Text>
        }

    } else {
        const value = props.itemValue(props.item, props.itemKey)
        if (value === undefined) {

            if( props.itemKey.toString() !== 'exposedAt' && props.itemKey.toString().includes('At') ) {
                cell = <Text>{convertTimezone(props.item[props.itemKey],props.timeZone)}</Text>
            }
            else{
                cell = <Text>{Types.present(props.item[props.itemKey])}</Text>
            }

        }
        else if (typeof value === 'string') {
            cell = <Text>{value}</Text>
        } else {
            cell = value
        }
    }



    return <>
        <TableCell >
            <TableCellLayout className={styles.tableCellLayoutNowrap}>
                {cell}
            </TableCellLayout>
        </TableCell>
    </>
}

type ListPaginationProps = {
    styles: ReturnType<typeof useStyles>
    size: number
    countState: UndefinedStatePair<bigint>
    offsetState: StatePair<bigint>
}

function ListPagination(props: ListPaginationProps) {
    const isLeadingTruncatedState = useBoolean(false)
    const isTrailingTruncatedState = useBoolean(false)
    const pageIndexState = useStatePair<bigint[]>([])
    const pageSizeState = useStatePair<bigint>(0n)
    const rowsPerPage = BigInt(props.size)

    useEffect(() => {
        const count = props.countState.value
        if (count === undefined) {
            pageIndexState.reset()
            return
        }

        const acc: bigint[] = []
        const currentPage = props.offsetState.value / rowsPerPage
        const pageSize = count % rowsPerPage === 0n
            ? (count / rowsPerPage)
            : (count / rowsPerPage + 1n)
        const evaluation = evaluatePageRange(20n, currentPage, pageSize - 1n)
        for (let i = evaluation.from; i <= evaluation.to; i++) {
            acc.push(i + 1n)
        }

        console.log('count', count, 'pageSize', pageSize, 'eval', evaluation, 'acc', acc)
        isLeadingTruncatedState.setValue(evaluation.isLeadingTruncated)
        isTrailingTruncatedState.setValue(evaluation.isTrailingTruncated)
        pageIndexState.setValue(acc)
        pageSizeState.setValue(pageSize)
    }, [
        rowsPerPage,
        props.size,
        props.countState.value,
        props.offsetState.value
    ]);

    return <>
        <Row className={props.styles.paginationContainer}>
            {isLeadingTruncatedState.value && <>
                <Button
                    className={props.styles.paginationButton}
                    appearance={'subtle'}
                    icon={<ChevronDoubleLeftFilled />}
                    onClick={() => props.offsetState.setValue(0n)} />
            </>}
            {pageIndexState.value.map(index => (
                <ToggleButton
                    key={`list-page-${index}`}
                    className={props.styles.paginationButton}
                    appearance={'subtle'}
                    checked={props.offsetState.value === (index - 1n) * rowsPerPage}
                    onClick={() => props.offsetState.setValue((index - 1n) * rowsPerPage)}>
                    {String(index)}
                </ToggleButton>
            ))}
            {isTrailingTruncatedState.value && <>
                <Button
                    className={props.styles.paginationButton}
                    appearance={'subtle'}
                    icon={<ChevronDoubleRightFilled />}
                    onClick={() => props.offsetState.setValue((pageSizeState.value - 1n) * rowsPerPage)} />
            </>}
        </Row>
    </>
}

function evaluatePageRange(
    itemCount: bigint,
    currentPageIndex: bigint,
    pageSize: bigint
): {
    from: bigint,
    to: bigint,
    isLeadingTruncated: boolean,
    isTrailingTruncated: boolean
} {
    console.log('pageSize', pageSize)
    if (currentPageIndex <= itemCount / 2n) {
        return {
            from: 0n,
            to: BigIntegers.min(itemCount, pageSize),
            isLeadingTruncated: false,
            isTrailingTruncated: pageSize >= itemCount
        }
    } else if (currentPageIndex >= pageSize - itemCount / 2n) {
        return {
            from: pageSize - itemCount,
            to: pageSize,
            isLeadingTruncated: pageSize >= itemCount,
            isTrailingTruncated: false
        }
    } else {
        return {
            from: currentPageIndex - itemCount / 2n,
            to: currentPageIndex + itemCount / 2n,
            isLeadingTruncated: pageSize >= itemCount,
            isTrailingTruncated: pageSize >= itemCount
        }
    }
}

type SearchConditionPanelProps<VO extends ValueObjectConstraint<Table>, Table> = {
    styles: ReturnType<typeof useStyles>
    description: Record<keyof VO, string>
    excluded: Set<keyof VO>
    panelState: StatePair<Panel>
    searchConditionsState: StatePair<SearchConditionElement<Table>[]>
}

function SearchConditionPanel<VO extends ValueObjectConstraint<Table>, Table>(props: SearchConditionPanelProps<VO, Table>) {
    const styles = props.styles
    const propertyKeyState = useUndefined<StringKeyOf<Table>>()
    const searchConditionState = useUndefined<SearchCondition>()
    const valueState = useInputString('')

    const onApplyClick = useCallback(() => {
        if (propertyKeyState.value === undefined) {
            return
        }
        if (searchConditionState.value === undefined) {
            return
        }

        const acc: SearchConditionElement<Table>[] = []
        acc.push(...props.searchConditionsState.value, {
            name: propertyKeyState.value,
            value: valueState.value,
            condition: searchConditionState.value
        })

        props.searchConditionsState.setValue(acc)
        props.panelState.setValue('List')
    }, [propertyKeyState.value, searchConditionState.value, valueState.value])
    const onCancelClick = useCallback(() => {
        props.panelState.setValue('List')
    }, [])
    useEffect(() => {
        if (props.panelState.value !== 'SearchCondition') {
            propertyKeyState.reset()
            searchConditionState.reset()
            valueState.reset()
        }
    }, [props.panelState.value])

    if (props.panelState.value !== 'SearchCondition') {
        return <></>
    }

    return <>
        <Column gap={'16px'}>
            <Row>
                <PropertyKeyList
                    styles={styles}
                    description={props.description}
                    excluded={props.excluded}
                    panel={'SearchCondition'}
                    state={propertyKeyState} />
                <SearchConditionList
                    styles={styles}
                    propertyKey={propertyKeyState.value}
                    searchConditionState={searchConditionState} />
                <SearchConditionInput
                    styles={styles}
                    propertyKey={propertyKeyState.value}
                    searchCondition={searchConditionState.value}
                    valueState={valueState} />
            </Row>
            <Row gap={'8px'}>
                <Button
                    appearance={'primary'}
                    disabled={propertyKeyState.value === undefined || searchConditionState.value === undefined}
                    onClick={onApplyClick}>
                    적용
                </Button>
                <Button
                    onClick={onCancelClick}>
                    취소
                </Button>
            </Row>
        </Column>
    </>
}

type SortOrderPanelProps<VO extends ValueObjectConstraint<Table>, Table> = {
    styles: ReturnType<typeof useStyles>
    description: Record<keyof VO, string>
    excluded: Set<keyof VO>
    panelState: StatePair<Panel>
    sortOrderState: StatePair<SortOrderElement<Table>[]>
}

function SortOrderPanel<VO extends ValueObjectConstraint<Table>, Table>(props: SortOrderPanelProps<VO, Table>) {
    const propertyKeyState = useUndefined<StringKeyOf<Table>>()
    const sortOrderKeyState = useUndefined<SortOrder>()

    const onApplyClick = useCallback(() => {
        if (propertyKeyState.value === undefined) {
            return
        }
        if (sortOrderKeyState.value === undefined) {
            return
        }

        const acc: SortOrderElement<Table>[] = []
        acc.push(...props.sortOrderState.value, {
            name: propertyKeyState.value,
            order: sortOrderKeyState.value
        })

        props.sortOrderState.setValue(acc)
        props.panelState.setValue('List')
    }, [props.sortOrderState.value, propertyKeyState.value, sortOrderKeyState.value])
    const onCancelClick = useCallback(() => {
        props.panelState.setValue('List')
    }, [])

    useEffect(() => {
        if (props.panelState.value !== 'SortOrder') {
            propertyKeyState.reset()
            sortOrderKeyState.reset()
        }
    }, [props.panelState.value])

    if (props.panelState.value !== 'SortOrder') {
        return <></>
    }

    return <>
        <Column gap={'16px'}>
            <Row>
                <PropertyKeyList
                    styles={props.styles}
                    description={props.description}
                    excluded={props.excluded}
                    panel={'SortOrder'}
                    state={propertyKeyState} />
                {propertyKeyState.value !== undefined && <>
                    <Divider
                        vertical
                        className={props.styles.filterDivider} />
                    <SortOrderList
                        styles={props.styles}
                        state={sortOrderKeyState} />
                </>}
            </Row>
            <Row gap={'8px'}>
                <Button
                    appearance={'primary'}
                    disabled={propertyKeyState.value === undefined || sortOrderKeyState.value === undefined}
                    onClick={onApplyClick}>
                    적용
                </Button>
                <Button
                    onClick={onCancelClick}>
                    취소
                </Button>
            </Row>
        </Column>
    </>
}

type PropertyListProps<VO extends ValueObjectConstraint<Table>, Table> = {
    styles: ReturnType<typeof useStyles>
    description: Record<keyof VO, string>
    excluded: Set<keyof VO>
    panel: 'SearchCondition' | 'SortOrder'
    state: UndefinedStatePair<StringKeyOf<Table>>
}

function PropertyKeyList<VO extends ValueObjectConstraint<Table>, Table>(props: PropertyListProps<VO, Table>) {
    return <>
        <Column>
            {Object.keys(props.description).map(key => !props.excluded.has(key as keyof VO) && (
                <PropertyKeyListItem
                    {...props}
                    key={`list-property-key-list-item-${key}`}
                    itemKey={key as StringKeyOf<Table>} />
            ))}
        </Column>
    </>
}

type PropertyListItemProps<VO extends ValueObjectConstraint<Table>, Table> = PropertyListProps<VO, Table> & {
    itemKey: StringKeyOf<Table>
}

function PropertyKeyListItem<VO extends ValueObjectConstraint<Table>, Table>(props: PropertyListItemProps<VO, Table>) {
    return <>
        <ToggleButton
            className={props.styles.filterListItemButton}
            appearance={'subtle'}
            checked={props.state.value === props.itemKey}
            onClick={() => props.state.setValue(props.itemKey)}>
            <Row alignItems={'center'} gap={'4px'}>
                {props.description[props.itemKey]}
            </Row>
        </ToggleButton>
    </>
}

type SearchConditionListProps<Table> = {
    styles: ReturnType<typeof useStyles>
    propertyKey: StringKeyOf<Table> | undefined
    searchConditionState: UndefinedStatePair<SearchCondition>
}

function SearchConditionList<Table>(props: SearchConditionListProps<Table>) {
    if (props.propertyKey === undefined) {
        return <></>
    }

    return <>
        <Divider
            vertical
            className={props.styles.filterDivider} />
        <Column>
            {Object.keys(SearchConditionDescription).map(key => (
                <SearchConditionListItem
                    {...props}
                    key={`list-search-condition-list-item-${key}`}
                    itemKey={key as SearchCondition} />
            ))}
        </Column>
    </>
}

type SearchConditionListItemProps<Table> = SearchConditionListProps<Table> & {
    itemKey: SearchCondition
}

function SearchConditionListItem<Table>(props: SearchConditionListItemProps<Table>) {
    return <>
        <ToggleButton
            key={`shopping-search-condition-list-${props.itemKey}`}
            className={props.styles.filterListItemButton}
            appearance={'subtle'}
            checked={props.searchConditionState.value === props.itemKey}
            onClick={() => props.searchConditionState.setValue(props.itemKey)}>
            <Row alignItems={'center'} gap={'4px'}>
                {SearchConditionDescription[props.itemKey]}
            </Row>
        </ToggleButton>
    </>
}

type SearchConditionInputProps<Table> = {
    styles: ReturnType<typeof useStyles>
    propertyKey: StringKeyOf<Table> | undefined
    searchCondition: SearchCondition | undefined
    valueState: InputStringStatePair
}

function SearchConditionInput<Table>(props: SearchConditionInputProps<Table>) {
    if (props.propertyKey === undefined) {
        return <></>
    }
    if (props.searchCondition === undefined) {
        return <></>
    }
    if (!SearchConditionValuePrerequisite[props.searchCondition]) {
        return <></>
    }

    return <>
        <Divider
            vertical
            className={props.styles.filterDivider} />
        <Column
            marginLeft={'12px'}
            marginRight={'12px'}>
            <Field
                required
                label={'값'}>
                <Input
                    value={props.valueState.value}
                    onChange={props.valueState.onChange} />
            </Field>
        </Column>
    </>
}

type SortOrderListProps = {
    styles: ReturnType<typeof useStyles>
    state: UndefinedStatePair<SortOrder>
}

function SortOrderList(props: SortOrderListProps) {
    return <>
        <Column>
            {Object.keys(SortOrderDescription).map(key => (
                <SortOrderListItem
                    {...props}
                    key={`list-sort-order-list-item-${key}`}
                    itemKey={key as SortOrder} />
            ))}
        </Column>
    </>
}

type SortOrderListItemProps = SortOrderListProps & {
    itemKey: SortOrder
}

function SortOrderListItem(props: SortOrderListItemProps) {
    const id = useId('shopping-list-')

    return <>
        <ToggleButton
            id={id}
            className={props.styles.filterListItemButton}
            appearance={'subtle'}
            checked={props.state.value === props.itemKey}
            onClick={() => props.state.setValue(props.itemKey)}>
            <Row
                alignItems={'center'}
                gap={'4px'}>
                {SortOrderDescription[props.itemKey]}
            </Row>
        </ToggleButton>
    </>
}